import type { FC } from 'react';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { featureToggleSelectors } from '@makemydeal/dr-shared-store';
import * as firstPencilActionCreators from '@makemydeal/dr-dash-store/actions/firstPencilActionCreators';
import * as firstPencilSelectors from '@makemydeal/dr-dash-store/selectors/firstPencilSelectors';
import type { ProgramQuote } from '@makemydeal/dr-dash-types';

import { ProgramsTable } from '../Programs/ProgramsTable';
import { FirstPencilSelectActionColumnBody } from './FirstPencilSelectActionColumnBody';
import { FirstPencilIncentivesTable } from './FirstPencilIncentivesTable';
import { toIncentiveSelection } from './utils';
import { useFirstPencilIncentiveRows } from './useFirstPencilIncentiveRows';
import type { FirstPencilProgramRow, FirstPencilProgramsOfferType } from './types';

type FirstPencilProgramsListProps = {
    data: FirstPencilProgramRow[];
    offerType: FirstPencilProgramsOfferType;
};

export const FirstPencilProgramsList: FC<FirstPencilProgramsListProps> = ({ data, offerType }) => {
    const dispatch = useDispatch();
    const isRestorationEnabled = useSelector(featureToggleSelectors.isProgramIncentivesRestorationEnabled);
    const selectedProgram = useSelector(firstPencilSelectors.getSelectedProgram);
    const incentives = useSelector(firstPencilSelectors.getProgramsIncentives);
    const selectedIncentives = useSelector(firstPencilSelectors.getSelectedIncentives);

    const selectedKey =
        selectedProgram?.offerType === offerType && data.some((row) => row.key === selectedProgram.program.key)
            ? (selectedProgram.program.key as string)
            : '';

    const selectedRow = useMemo(() => data.find((row) => row.key === selectedKey), [data, selectedKey]);

    const hasPickForTab = selectedProgram?.offerType === offerType;
    const [awaitingPersistedPick, setAwaitingPersistedPick] = useState(hasPickForTab);
    const [orderingKey, setOrderingKey] = useState('');
    if (awaitingPersistedPick && selectedKey) {
        setAwaitingPersistedPick(false);
        setOrderingKey(selectedKey);
    } else if (awaitingPersistedPick && !hasPickForTab) {
        setAwaitingPersistedPick(false);
    }

    const orderedData = useMemo(() => {
        if (!isRestorationEnabled || !orderingKey) return data;
        const index = data.findIndex((row) => row.key === orderingKey);
        if (index <= 0) return data;
        return [data[index], ...data.slice(0, index), ...data.slice(index + 1)];
    }, [data, orderingKey, isRestorationEnabled]);

    const incentiveRows = useFirstPencilIncentiveRows(selectedRow, offerType);

    useEffect(() => {
        if (selectedRow && incentives && !selectedIncentives) {
            dispatch(firstPencilActionCreators.incentivesSelected(toIncentiveSelection(incentiveRows)));
        }
    }, [dispatch, selectedRow, incentives, selectedIncentives, incentiveRows]);

    const renderSelect = useCallback(
        (_text: unknown, value: ProgramQuote & { key?: string }, index: number) => (
            <FirstPencilSelectActionColumnBody
                value={value}
                index={index}
                offerType={offerType}
                isSelected={value.key === selectedKey}
            />
        ),
        [offerType, selectedKey]
    );

    const renderExpandedRow = useCallback(() => <FirstPencilIncentivesTable incentives={incentiveRows} />, [incentiveRows]);

    return (
        <ProgramsTable
            testId={`first-pencil-programs-list-${offerType}`}
            data={orderedData}
            offerType={offerType}
            renderSelect={renderSelect}
            pinnedKey={isRestorationEnabled ? orderingKey : selectedKey}
            expandedKey={selectedKey}
            renderExpandedRow={renderExpandedRow}
        />
    );
};
